import React from "react";
import Modal from "../Modal";
import { AdminTenant, AdminUser, UpdateUserPayload } from "../../services/api";
import "./UserManagementModals.css";

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  selectedUser: AdminUser | null;
  updateFormData: UpdateUserPayload;
  setUpdateFormData: React.Dispatch<React.SetStateAction<UpdateUserPayload>>;
  tenants: AdminTenant[];
  isSuperadmin: boolean;
  formError: string;
  isSubmitting: boolean;
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  selectedUser,
  updateFormData,
  setUpdateFormData,
  tenants,
  isSuperadmin,
  formError,
  isSubmitting,
}) => {
  if (!selectedUser) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit User" maxWidth="500px">
      <div className="user-modal-content">
        {formError && <div className="user-modal-error">{formError}</div>}
        <form onSubmit={onSubmit}>
          <div className="user-form-group">
            <label>Email</label>
            <input
              type="email"
              value={updateFormData.email || ""}
              onChange={(e) =>
                setUpdateFormData({ ...updateFormData, email: e.target.value })
              }
              placeholder="user@example.com"
            />
          </div>
          {isSuperadmin && (
            <div className="user-form-group">
              <label>New Password (leave blank to keep current)</label>
              <input
                type="password"
                value={updateFormData.new_password || ""}
                onChange={(e) =>
                  setUpdateFormData({
                    ...updateFormData,
                    new_password: e.target.value || undefined,
                  })
                }
                placeholder="••••••••"
                minLength={8}
              />
            </div>
          )}
          {isSuperadmin && (
            <div className="user-form-group">
              <label>Tenant</label>
              <select
                value={updateFormData.tenant_id || ""}
                onChange={(e) =>
                  setUpdateFormData({ ...updateFormData, tenant_id: e.target.value })
                }
              >
                {tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="user-form-group">
            <label>Role</label>
            <select
              value={updateFormData.role || "user"}
              onChange={(e) =>
                setUpdateFormData({ ...updateFormData, role: e.target.value })
              }
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="user-form-group user-form-checkbox">
            <label htmlFor="edit_is_active">
              <input
                type="checkbox"
                id="edit_is_active"
                checked={updateFormData.is_active ?? true}
                onChange={(e) =>
                  setUpdateFormData({
                    ...updateFormData,
                    is_active: e.target.checked,
                  })
                }
              />
              <span className="user-checkbox-custom"></span>
              <span className="user-checkbox-label">Active</span>
            </label>
          </div>
          {isSuperadmin && (
            <div className="user-form-group user-form-checkbox">
              <label htmlFor="edit_is_superadmin">
                <input
                  type="checkbox"
                  id="edit_is_superadmin"
                  checked={updateFormData.is_superadmin ?? false}
                  onChange={(e) =>
                    setUpdateFormData({
                      ...updateFormData,
                      is_superadmin: e.target.checked,
                    })
                  }
                />
                <span className="user-checkbox-custom"></span>
                <span className="user-checkbox-label">Superadmin privileges</span>
              </label>
            </div>
          )}
          <div className="user-modal-actions">
            <button
              type="button"
              className="user-modal-btn secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="user-modal-btn primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default EditUserModal;
